import { useState } from "react"

import { GoogleAuthProvider, OAuthProvider, signInWithRedirect } from "firebase/auth"
import { Loader } from "lucide-react"
import { toast } from "sonner"

import { CONFIGURATION } from "@env/variables"

import { useUXTracking } from "@hooks/use-ux-tracking"

import { firebaseAuth, setFirebaseTenant } from "@session/auth/firebase"

import { Button } from "@ui/button"
import { Input } from "@ui/input"

function getProvider(redirectTo?: string) {
    const providerType = CONFIGURATION.firebase.providerType
    switch (providerType) {
        case "oidc":
            return new OAuthProvider("oidc.main")
        case "google": {
            let redirectPath = redirectTo || window.location.pathname || "/"
            if (redirectPath[0] !== "/") {
                redirectPath = "/" + redirectPath
            }
            const provider = new GoogleAuthProvider()
            provider.addScope("profile")
            provider.addScope("email")
            provider.setCustomParameters({
                prompt: "select_account",
                redirect_uri: window.location.origin + redirectPath,
            })
            return provider
        }
        case "microsoft":
            return new OAuthProvider("microsoft.com")
        case "github":
            return new OAuthProvider("github.com")
        default:
            return new OAuthProvider("oidc.main")
    }
}

interface EnterpriseSignInButtonProps {
    redirectTo?: string
}
export function EnterpriseSignInButton({ redirectTo }: Readonly<EnterpriseSignInButtonProps>) {
    const [isLoading, setIsLoading] = useState(false)
    const { trackEvent } = useUXTracking()

    const [email, setEmail] = useState<string | undefined>("")

    const checkEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return email && emailRegex.test(email)
    }

    const signIn = () => {
        if (!email) {
            return
        }

        if (!checkEmail()) {
            toast.error("Invalid email address", {
                description: "Please enter a valid email address",
                duration: 5000,
            })
            return
        }

        setIsLoading(true)
        setFirebaseTenant(email).then((data) => {
            if (!data) {
                toast.error("Tenant not found", {
                    description: "Provided tenant not found. Please check your email address.",
                    duration: 5000,
                })
                setIsLoading(false)
                return
            }
            localStorage.setItem("tenantName", data.name ?? "")
            const provider = getProvider(redirectTo)
            trackEvent("enterprise_signin", {
                firebaseTenantName: data.name,
            })

            signInWithRedirect(firebaseAuth, provider)
            setIsLoading(false)
        })
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setEmail(e.target.value)
    }

    return (
        <>
            <Input value={email} onChange={handleInputChange} placeholder="Enter your enterprise email" type="email" />
            <Button variant={"outline"} onClick={signIn} className="w-full mt-2 py-4 flex rounded-lg hover:shadow-sm transition duration-150 text-center" disabled={!checkEmail() || isLoading}>
                {isLoading ? (
                    <div className="w-full flex justify-center">
                        <Loader className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                    </div>
                ) : (
                    <span className="text-sm font-semibold">Continue with Enterprise SSO</span>
                )}
            </Button>
        </>
    )
}
